// Lead intake for the marketing site's "book a demo" form (decision D-72).
//
// Contract with the intake endpoint (POST JSON, cross-origin, preflighted):
//   200 {ok:true}   success, always — including submissions the server quietly discards
//   400 {error}     a field failed validation
//   403             this origin is not on the endpoint's ALLOWED_ORIGINS allowlist; a config
//                   problem on the intake side, not something to retry
//
// The field names, the payload shape, and the localStorage key are validated by the endpoint and
// mirrored by the Vercel landing page. Do not rename fields, add fields, or change the key.

// Until a custom subdomain (e.g. leads.datafabriq.co) is pointed at the Vercel project.
export const LEAD_INTAKE_URL = "https://datafabriq-landing.vercel.app/api/lead";

// page_slug is set explicitly per placement, never derived from the pathname. Any new slug needs a
// row in the Attribution Rules table in Attio first, or bare arrivals on that page report as Direct.
export type LeadPageSlug = "/lp/data-platform" | "/lp/integrations";

const FIRST_TOUCH_STORAGE_KEY = "df_first_touch";

// Written once per browser by the inline first-touch script in content-website/index.html. Every
// field is absent when the first arrival carried no such signal (bare direct visit, no UTM tags).
interface FirstTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  referrer_url?: string;
  landing_path?: string;
  first_seen_at?: string;
}

export interface LeadFormValues {
  first_name: string;
  last_name: string;
  email: string;
  company_name: string;
  job_title: string;
  pain: string;
  // Honeypot. Visually hidden on the form; must reach the endpoint empty.
  website: string;
}

export interface LeadIntakePayload extends LeadFormValues {
  page_slug: LeadPageSlug;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  referrer_url: string;
  landing_path: string;
  first_seen_at: string;
}

export interface LeadConversionInfo {
  page_slug: LeadPageSlug;
  utm_source: string;
}

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

function readFirstTouch(): FirstTouch {
  try {
    return JSON.parse(localStorage.getItem(FIRST_TOUCH_STORAGE_KEY) ?? "{}") as FirstTouch;
  } catch {
    return {};
  }
}

/**
 * Builds the intake payload. Reads storage and the query string at SUBMIT time: in a single-page
 * app the URL changes without a reload, and the first-touch fallback must see the current one.
 */
export function buildLeadIntakePayload(values: LeadFormValues, pageSlug: LeadPageSlug): LeadIntakePayload {
  const stored = readFirstTouch();
  const params = new URLSearchParams(window.location.search);

  return {
    ...values,
    page_slug: pageSlug,
    utm_source: stored.utm_source ?? params.get("utm_source") ?? "",
    utm_medium: stored.utm_medium ?? params.get("utm_medium") ?? "",
    utm_campaign: stored.utm_campaign ?? params.get("utm_campaign") ?? "",
    utm_content: stored.utm_content ?? params.get("utm_content") ?? "",
    referrer_url: stored.referrer_url ?? "",
    landing_path: stored.landing_path ?? window.location.pathname,
    first_seen_at: stored.first_seen_at ?? "",
  };
}

/**
 * Posts the payload to the intake endpoint. Plain JSON content type only — a custom header or
 * credentials mode would fail the endpoint's CORS preflight. Resolves on 200; throws on any other
 * status or on network failure, and the form shows its generic error note in both cases.
 */
export async function submitLeadIntake(payload: LeadIntakePayload): Promise<void> {
  const response = await fetch(LEAD_INTAKE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Lead intake failed with status ${response.status}`);
  }
}
